import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  fetchTrackedKeywords,
  addKeywords,
  deleteKeyword,
  batchDeleteKeywords,
  triggerRankCheck,
  fetchRankHistory,
  fetchLocationComparison,
  fetchSerpSnapshot,
  fetchRankingSummary,
  fetchCheckStatus,
  collectRankResults,
  clearStuckTasks,
  fetchCredentialsCheck,
} from '../api/rankings';
import { fetchProjects } from '../api/projects';
import LocationPicker from '../components/LocationPicker';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  TrendingDown,
  Plus,
  Trash2,
  RefreshCw,
  Search,
  Monitor,
  Smartphone,
  ArrowUp,
  ArrowDown,
  Minus,
  X,
  ChevronDown,
  Globe,
  Target,
  BarChart3,
  ExternalLink,
  AlertTriangle,
  Settings as SettingsIcon,
  Info,
  Loader2,
  CheckCircle2,
  Copy,
  Filter,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

function PositionBadge({ position }) {
  if (position == null) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  return (
    <span className={cn(
      'font-semibold tabular-nums',
      position <= 3 ? 'text-green-600' : position <= 10 ? 'text-blue-600' : position <= 20 ? 'text-yellow-600' : 'text-muted-foreground'
    )}>
      {position}
    </span>
  );
}

function ChangeBadge({ change }) {
  if (change == null) return <span className="text-muted-foreground text-sm">—</span>;
  if (change === 0) return <Minus className="h-3 w-3 text-muted-foreground mx-auto" />;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 text-xs font-medium',
      change < 0 ? 'text-green-600' : 'text-red-500'
    )}>
      {change < 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(change)}
    </span>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        {Icon && <Icon className={cn('h-4 w-4', color || 'text-muted-foreground')} />}
      </div>
      <div className="text-2xl font-bold">{value ?? '—'}</div>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

export default function Rankings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedSerpKeyword, setSelectedSerpKeyword] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [positionFilter, setPositionFilter] = useState(''); // '', 'top3', 'top10', 'top20', 'top50', '50+', 'unranked'

  // ── Polling for async batch results ──
  const [polling, setPolling] = useState(false);
  const [pollStatus, setPollStatus] = useState(null); // { collected, still_pending }
  const pollIntervalRef = useRef(null);

  const projectId = searchParams.get('project_id') || '';

  // Credentials check
  const { data: credsData } = useQuery({
    queryKey: ['dfs-credentials-check'],
    queryFn: fetchCredentialsCheck,
  });
  const hasCredentials = credsData?.has_credentials ?? true; // Assume true until loaded

  // Fetch projects
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });
  const projectsList = projectsData?.projects || [];

  // Fetch keywords (auto-refetch every 10s when polling for batch results)
  const { data: keywordsData, isLoading: keywordsLoading } = useQuery({
    queryKey: ['tracked-keywords', projectId, deviceFilter, locationFilter],
    queryFn: () => fetchTrackedKeywords({ project_id: projectId || undefined, device: deviceFilter || undefined, location_code: locationFilter || undefined }),
    enabled: true,
    refetchInterval: polling ? 10000 : false,
  });
  const keywords = keywordsData?.keywords || [];
  const availableLocations = keywordsData?.available_locations || [];

  // Fetch summary — always enabled, no project_id required
  const { data: summaryData } = useQuery({
    queryKey: ['rankings-summary', projectId],
    queryFn: () => fetchRankingSummary(projectId || undefined),
    enabled: true,
  });
  const summary = summaryData || {};

  // Fetch rank history for expanded keyword
  const { data: historyData } = useQuery({
    queryKey: ['rank-history', expandedId],
    queryFn: () => fetchRankHistory(expandedId, 30),
    enabled: !!expandedId,
  });

  // Fetch SERP snapshot for selected keyword
  const { data: serpData } = useQuery({
    queryKey: ['serp-snapshot', selectedSerpKeyword],
    queryFn: () => fetchSerpSnapshot(selectedSerpKeyword),
    enabled: !!selectedSerpKeyword,
  });

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPolling(false);
    setPollStatus(null);
  }, []);

  const startPolling = useCallback((pid) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      clearTimeout(pollIntervalRef.current);
    }
    setPolling(true);
    setPollStatus({ collected: 0, still_pending: '...' });

    const poll = async () => {
      try {
        const result = await collectRankResults(pid || undefined);
        setPollStatus({ collected: result.collected, still_pending: result.still_pending });
        queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
        queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });

        if (result.still_pending === 0) {
          toast.success(`All results collected! ${result.collected} keyword(s) updated.`);
          stopPolling();
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    // Initial delay of 15s (DFS needs time), then every 15s
    const initialTimer = setTimeout(() => {
      poll();
      pollIntervalRef.current = setInterval(poll, 15000);
    }, 15000);

    // Store initial timer so we can clean it up
    pollIntervalRef.current = initialTimer;
  }, [queryClient, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        clearTimeout(pollIntervalRef.current);
      }
    };
  }, []);

  // Mutations
  const checkMutation = useMutation({
    mutationFn: triggerRankCheck,
    onSuccess: (data) => {
      const providerLabel = data.provider === 'scrapingrobot' ? ' via SR' : '';
      if (data.mode === 'async') {
        toast.success(`Submitted ${data.tasks_submitted} tasks. Collecting results automatically...`);
        startPolling(projectId);
      } else if (data.errors?.length && data.checked > 0) {
        toast.success(`Checked ${data.checked} keyword(s)${providerLabel}, ${data.errors.length} failed`);
        // Show individual errors
        for (const e of data.errors.slice(0, 3)) {
          toast.error(`"${e.keyword}": ${e.error}`, { duration: 6000 });
        }
      } else if (data.errors?.length && data.checked === 0) {
        toast.error(`All ${data.errors.length} keyword(s) failed to check`);
        for (const e of data.errors.slice(0, 3)) {
          toast.error(`"${e.keyword}": ${e.error}`, { duration: 6000 });
        }
      } else {
        toast.success(`Checked ${data.checked || 1} keyword(s)${providerLabel}`);
      }
      queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });
    },
    onError: (err) => {
      const errData = err.response?.data;
      if (errData?.code === 'NO_DFS_CREDENTIALS') {
        toast.error('Configure DataForSEO credentials in Integrations first', { duration: 5000 });
      } else if (errData?.code === 'NO_SR_CREDENTIALS') {
        toast.error('Configure Scraping Robot token in Integrations first', { duration: 5000 });
      } else if (errData?.code === 'CHECK_COOLDOWN') {
        toast.error(errData.message || 'Keywords were checked recently. Try again later.', { duration: 6000 });
      } else {
        toast.error(errData?.error || 'Check failed');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteKeyword,
    onSuccess: () => {
      toast.success('Keyword deleted');
      queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: batchDeleteKeywords,
    onSuccess: (data) => {
      toast.success(data.message);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });
    },
  });

  // Filter keywords by search + position
  const filteredKeywords = useMemo(() => {
    let result = keywords;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(k => k.keyword.toLowerCase().includes(q));
    }
    if (positionFilter) {
      result = result.filter(k => {
        const p = k.current_position;
        switch (positionFilter) {
          case 'top3': return p != null && p <= 3;
          case 'top10': return p != null && p <= 10;
          case 'top20': return p != null && p <= 20;
          case 'top50': return p != null && p <= 50;
          case '50+': return p != null && p > 50;
          case 'unranked': return p == null;
          default: return true;
        }
      });
    }
    return result;
  }, [keywords, searchQuery, positionFilter]);

  // Chart data for overview (top 5 keywords)
  const currentProject = projectsList.find(p => p.id === projectId);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredKeywords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredKeywords.map(k => k.id)));
    }
  };

  // History chart data
  const historyChartData = useMemo(() => {
    if (!historyData?.history) return [];
    return historyData.history.map(h => ({
      date: new Date(h.checked_at).toLocaleDateString(),
      position: h.position,
    }));
  }, [historyData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Keyword Rankings</h1>
          <p className="text-sm text-muted-foreground mt-1">Track keyword positions across locations and devices</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Project selector */}
          <select
            value={projectId}
            onChange={(e) => setSearchParams(e.target.value ? { project_id: e.target.value } : {})}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
          >
            <option value="">All Projects</option>
            {projectsList.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {/* Show collect/clear buttons when there are pending tasks */}
          {!polling && keywords.some(k => k.dfs_task_id) && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => startPolling(projectId)}
                className="flex items-center gap-2 px-3 py-2 border border-blue-300 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
              >
                <RefreshCw className="h-4 w-4" /> Collect Results ({keywords.filter(k => k.dfs_task_id).length} pending)
              </button>
              <button
                onClick={async () => {
                  if (!confirm(`Clear ${keywords.filter(k => k.dfs_task_id).length} stuck pending task(s)? This won't fetch results — it just unsticks the keywords so you can re-check them.`)) return;
                  try {
                    const result = await clearStuckTasks();
                    toast.success(result.message);
                    queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
                  } catch (err) {
                    toast.error('Failed to clear stuck tasks');
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 border border-amber-300 text-amber-700 dark:text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                title="Clear stuck task IDs without fetching results"
              >
                <X className="h-4 w-4" /> Clear Stuck
              </button>
            </div>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Keywords
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'keywords', label: 'Keywords', icon: Target },
          { id: 'serp', label: 'SERP Details', icon: Globe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Credentials warning banner */}
      {hasCredentials === false && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">DataForSEO credentials not configured</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Add your API credentials in Integrations to enable rank checking.</p>
          </div>
          <a href="/integrations" className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
            Go to Integrations →
          </a>
        </div>
      )}

      {/* Polling banner */}
      {polling && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm">
          <RefreshCw className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0" />
          <span className="text-blue-700 dark:text-blue-300">
            Collecting rank results from DataForSEO...
            {pollStatus && ` ${pollStatus.collected} collected, ${pollStatus.still_pending} pending`}
          </span>
          <button onClick={stopPolling} className="ml-auto text-blue-500 hover:text-blue-700 text-xs font-medium">
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          summary={summary}
          projectId={projectId}
          keywords={keywords}
          hasCredentials={hasCredentials}
          onAddKeywords={() => setShowAddModal(true)}
        />
      )}

      {activeTab === 'keywords' && (
        <KeywordsTab
          keywords={filteredKeywords}
          allKeywords={keywords}
          loading={keywordsLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          deviceFilter={deviceFilter}
          setDeviceFilter={setDeviceFilter}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          availableLocations={availableLocations}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect}
          toggleSelectAll={toggleSelectAll}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
          historyChartData={historyChartData}
          projectId={projectId}
          onCheck={(ids) => checkMutation.mutate({ keyword_ids: ids, project_id: projectId })}
          onDelete={(id) => deleteMutation.mutate(id)}
          onBatchDelete={() => batchDeleteMutation.mutate([...selectedIds])}
          onCopyToLocation={() => setShowCopyModal(true)}
          checkLoading={checkMutation.isPending}
          hasCredentials={hasCredentials}
        />
      )}

      {activeTab === 'serp' && (
        <SerpTab
          keywords={keywords}
          selectedKeyword={selectedSerpKeyword}
          setSelectedKeyword={setSelectedSerpKeyword}
          serpData={serpData}
          projectId={projectId}
          currentDomain={currentProject?.target_domain}
        />
      )}

      {/* Add Keywords Modal */}
      {showAddModal && (
        <AddKeywordsModal
          projectId={projectId}
          projects={projectsList}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
            queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });
          }}
        />
      )}

      {/* Copy Keywords to Location Modal */}
      {showCopyModal && (
        <CopyKeywordsModal
          selectedKeywords={keywords.filter(k => selectedIds.has(k.id))}
          projectId={projectId}
          projects={projectsList}
          onClose={() => setShowCopyModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tracked-keywords'] });
            queryClient.invalidateQueries({ queryKey: ['rankings-summary'] });
            setSelectedIds(new Set());
            setShowCopyModal(false);
          }}
        />
      )}
    </div>
  );
}

function OverviewTab({ summary, projectId, keywords, hasCredentials, onAddKeywords }) {
  // Build chart data from top 5 keywords (using their current data point)
  const top5 = keywords
    .filter(k => k.current_position != null)
    .sort((a, b) => a.current_position - b.current_position)
    .slice(0, 5);

  // Position distribution buckets
  const distribution = useMemo(() => {
    const buckets = { top3: 0, top10: 0, top20: 0, top50: 0, beyond: 0, unranked: 0 };
    for (const kw of keywords) {
      const p = kw.current_position;
      if (p == null) buckets.unranked++;
      else if (p <= 3) buckets.top3++;
      else if (p <= 10) buckets.top10++;
      else if (p <= 20) buckets.top20++;
      else if (p <= 50) buckets.top50++;
      else buckets.beyond++;
    }
    return buckets;
  }, [keywords]);

  const pieData = [
    { name: 'Top 3', value: distribution.top3, color: '#22c55e' },
    { name: 'Top 10', value: distribution.top10, color: '#3b82f6' },
    { name: 'Top 20', value: distribution.top20, color: '#eab308' },
    { name: 'Top 50', value: distribution.top50, color: '#f97316' },
    { name: '50+', value: distribution.beyond, color: '#ef4444' },
    { name: 'Not Ranked', value: distribution.unranked, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Keywords" value={summary.total || 0} icon={Target} color="text-blue-500" />
        <StatCard title="Avg Position" value={summary.avg_position} icon={BarChart3} color="text-purple-500" />
        <StatCard
          title="Improved"
          value={summary.improved || 0}
          icon={TrendingUp}
          color="text-green-500"
          subtitle={`${summary.declined || 0} declined`}
        />
        <StatCard
          title="Declined"
          value={summary.declined || 0}
          icon={TrendingDown}
          color="text-red-500"
          subtitle={`${summary.unchanged || 0} unchanged`}
        />
      </div>

      {/* Position Distribution donut */}
      {keywords.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Position Distribution</h3>
          <div className="flex items-center gap-8">
            <div className="w-40 h-40 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={2}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-muted-foreground">{d.name}</span>
                  <span className="font-semibold">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Smart empty state when no keywords */}
      {keywords.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-border bg-card/50 p-8 text-center">
          <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-1">No keywords tracked yet</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Start tracking your keyword rankings by adding keywords to a project.
            {!hasCredentials && " You'll also need to configure DataForSEO credentials in Integrations."}
          </p>
          <div className="flex items-center justify-center gap-3">
            {!hasCredentials && (
              <a href="/integrations" className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors">
                <SettingsIcon className="h-4 w-4" /> Configure Credentials
              </a>
            )}
            <button
              onClick={onAddKeywords}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Keywords
            </button>
          </div>
        </div>
      )}

      {/* Top Movers */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Gainers */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold text-green-600 mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Top Gainers
          </h3>
          {(summary.top_gainers || []).length ? (
            <div className="space-y-2">
              {summary.top_gainers.map(kw => (
                <div key={kw.id} className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{kw.keyword}</span>
                  <div className="flex items-center gap-2">
                    <PositionBadge position={kw.current_position} />
                    <ChangeBadge change={kw.position_change} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data yet</p>
          )}
        </div>

        {/* Losers */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold text-red-500 mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4" /> Top Losers
          </h3>
          {(summary.top_losers || []).length ? (
            <div className="space-y-2">
              {summary.top_losers.map(kw => (
                <div key={kw.id} className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{kw.keyword}</span>
                  <div className="flex items-center gap-2">
                    <PositionBadge position={kw.current_position} />
                    <ChangeBadge change={kw.position_change} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data yet</p>
          )}
        </div>
      </div>

      {/* Top 5 positions */}
      {top5.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Top Ranked Keywords</h3>
          <div className="space-y-2">
            {top5.map(kw => (
              <div key={kw.id} className="flex items-center gap-3 text-sm">
                <PositionBadge position={kw.current_position} />
                <span className="flex-1 truncate">{kw.keyword}</span>
                <span className="text-xs text-muted-foreground">{kw.location_name}</span>
                <span className="text-xs text-muted-foreground capitalize">{kw.device}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KeywordsTab({
  keywords, allKeywords, loading, searchQuery, setSearchQuery, deviceFilter, setDeviceFilter,
  locationFilter, setLocationFilter, positionFilter, setPositionFilter,
  availableLocations,
  selectedIds, toggleSelect, toggleSelectAll, expandedId, setExpandedId,
  historyChartData, projectId, onCheck, onDelete, onBatchDelete, onCopyToLocation,
  checkLoading, hasCredentials,
}) {
  const activeFilterCount = [deviceFilter, locationFilter, positionFilter].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        {/* Row 1: Search + Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search keywords..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
            />
          </div>

          {/* Selection actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-medium">{selectedIds.size} selected</span>
              <button
                onClick={() => hasCredentials ? onCheck([...selectedIds]) : toast.error('Configure SERP credentials in Integrations first')}
                disabled={checkLoading || !hasCredentials}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', checkLoading && 'animate-spin')} />
                Check Now
              </button>
              <button
                onClick={onCopyToLocation}
                className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors"
                title="Copy selected keywords to a different location"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy to Location
              </button>
              <button
                onClick={onBatchDelete}
                className="flex items-center gap-1.5 px-3 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm hover:bg-destructive/90"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          )}
        </div>

        {/* Row 2: Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={deviceFilter}
            onChange={(e) => setDeviceFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
          >
            <option value="">All Devices</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
          </select>
          {availableLocations.length > 1 && (
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
            >
              <option value="">All Locations</option>
              {availableLocations.map(loc => (
                <option key={loc.code} value={loc.code}>{loc.name}</option>
              ))}
            </select>
          )}
          <select
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
          >
            <option value="">All Positions</option>
            <option value="top3">Top 3</option>
            <option value="top10">Top 10</option>
            <option value="top20">Top 20</option>
            <option value="top50">Top 50</option>
            <option value="50+">50+</option>
            <option value="unranked">Not Ranked</option>
          </select>
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setDeviceFilter(''); setLocationFilter(''); setPositionFilter(''); }}
              className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
            >
              <X className="h-3 w-3" /> Clear filters ({activeFilterCount})
            </button>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {keywords.length}{keywords.length !== (allKeywords?.length || 0) ? ` of ${allKeywords?.length || 0}` : ''} keywords
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === keywords.length && keywords.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-border"
                  />
                </th>
                <th className="px-3 py-3 text-left font-medium">Keyword</th>
                <th className="px-3 py-3 text-left font-medium hidden sm:table-cell">Location</th>
                <th className="px-3 py-3 text-center font-medium hidden sm:table-cell">
                  <span className="inline-flex items-center gap-1"><Monitor className="h-3.5 w-3.5" /> Device</span>
                </th>
                <th className="px-3 py-3 text-center font-medium">Position</th>
                <th className="px-3 py-3 text-left font-medium hidden sm:table-cell">Ranking URL</th>
                <th className="px-3 py-3 text-center font-medium">Change</th>
                <th className="px-3 py-3 text-center font-medium hidden sm:table-cell">Best</th>
                <th className="px-3 py-3 text-left font-medium">Last Checked</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : keywords.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">No keywords tracked yet</td></tr>
              ) : keywords.map(kw => (
                <KeywordRow
                  key={kw.id}
                  kw={kw}
                  selected={selectedIds.has(kw.id)}
                  onToggle={() => toggleSelect(kw.id)}
                  expanded={expandedId === kw.id}
                  onExpand={() => setExpandedId(expandedId === kw.id ? null : kw.id)}
                  historyChartData={expandedId === kw.id ? historyChartData : []}
                  onDelete={() => onDelete(kw.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KeywordRow({ kw, selected, onToggle, expanded, onExpand, historyChartData, onDelete }) {
  return (
    <>
      <tr
        className={cn('hover:bg-muted/30 cursor-pointer transition-colors', expanded && 'bg-muted/20')}
        onClick={onExpand}
      >
        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onToggle} className="rounded border-border" />
        </td>
        <td className="px-3 py-3 font-medium">{kw.keyword}</td>
        <td className="px-3 py-3 text-muted-foreground hidden sm:table-cell">{kw.location_name}</td>
        <td className="px-3 py-3 text-center hidden sm:table-cell">
          {kw.device === 'mobile' ? <Smartphone className="h-4 w-4 mx-auto text-muted-foreground" /> : <Monitor className="h-4 w-4 mx-auto text-muted-foreground" />}
        </td>
        <td className="px-3 py-3 text-center">
          <PositionBadge position={kw.current_position} />
        </td>
        <td className="px-3 py-3 hidden sm:table-cell">
          {kw.current_url ? (
            <a
              href={kw.current_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1 max-w-[200px] truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => { try { return new URL(kw.current_url).pathname; } catch { return kw.current_url; } })()}
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-3 py-3 text-center">
          <ChangeBadge change={kw.position_change} />
        </td>
        <td className="px-3 py-3 text-center font-medium text-muted-foreground hidden sm:table-cell">
          {kw.best_position ?? '—'}
        </td>
        <td className="px-3 py-3">
          {kw.last_checked ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">
                {new Date(kw.last_checked).toLocaleDateString()}
              </span>
              {kw.position_change != null && kw.position_change !== 0 && (
                <span className={cn(
                  'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold',
                  kw.position_change < 0
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                )}>
                  {kw.position_change < 0 ? '↑' : '↓'}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">Never</span>
          )}
        </td>
        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <button onClick={onDelete} className="p-1 text-muted-foreground hover:text-destructive rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={10} className="px-6 py-4 bg-muted/10">
            {historyChartData.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis reversed domain={['auto', 'auto']} tick={{ fontSize: 11 }} label={{ value: 'Position', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="position" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No history data yet. Run a rank check first.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function SerpTab({ keywords, selectedKeyword, setSelectedKeyword, serpData, projectId, currentDomain }) {
  const serpSnapshot = serpData?.serp;
  const [serpSearch, setSerpSearch] = useState('');

  const filteredSerpKeywords = keywords.filter(kw =>
    !serpSearch || kw.keyword.toLowerCase().includes(serpSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Keyword selector with search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={serpSearch}
            onChange={(e) => setSerpSearch(e.target.value)}
            placeholder="Filter keywords..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
          />
        </div>
        <select
          value={selectedKeyword || ''}
          onChange={(e) => setSelectedKeyword(e.target.value || null)}
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm w-full sm:max-w-md"
        >
          <option value="">Select a keyword...</option>
          {filteredSerpKeywords.map(kw => (
            <option key={kw.id} value={kw.id}>
              {kw.keyword} ({kw.location_name}, {kw.device})
            </option>
          ))}
        </select>
      </div>

      {!selectedKeyword && (
        <div className="rounded-xl border-2 border-dashed border-border bg-card/50 p-8 text-center">
          <Globe className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-1">SERP Analysis</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Select a keyword above to view its SERP snapshot, including top 10 results and SERP features.
            Run a rank check first to populate SERP data.
          </p>
        </div>
      )}

      {selectedKeyword && serpSnapshot && (
        <div className="space-y-4">
          {/* SERP features */}
          {serpSnapshot.serp_features?.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-2">SERP Features</h3>
              <div className="flex flex-wrap gap-2">
                {serpSnapshot.serp_features.map(f => (
                  <span key={f} className="px-2 py-1 bg-muted rounded-md text-xs font-medium">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top results table */}
          {serpSnapshot.top_results?.length > 0 && (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-3 bg-muted/50 border-b border-border">
                <h3 className="text-sm font-semibold">SERP Results</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-2 text-left font-medium w-16">#</th>
                      <th className="px-4 py-2 text-left font-medium">Domain</th>
                      <th className="px-4 py-2 text-left font-medium">Title</th>
                      <th className="px-4 py-2 text-left font-medium">URL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {serpSnapshot.top_results.map((r, i) => {
                      const isUser = currentDomain && r.domain?.toLowerCase().replace(/^www\./, '') === currentDomain.toLowerCase().replace(/^www\./, '');
                      return (
                        <tr key={i} className={cn(isUser && 'bg-primary/5 font-medium')}>
                          <td className="px-4 py-2">{r.position}</td>
                          <td className="px-4 py-2">
                            <span className={cn(isUser && 'text-primary')}>{r.domain}</span>
                          </td>
                          <td className="px-4 py-2 max-w-[250px] truncate">{r.title}</td>
                          <td className="px-4 py-2">
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 max-w-[200px] truncate">
                              {(() => { try { return new URL(r.url).pathname; } catch { return r.url; } })()}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedKeyword && !serpSnapshot && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
          No SERP data available. Run a rank check first.
        </div>
      )}
    </div>
  );
}

function CopyKeywordsModal({ selectedKeywords, projectId, projects, onClose, onSuccess }) {
  const [targetProjectId, setTargetProjectId] = useState(projectId || '');
  const [location, setLocation] = useState({ location_code: 2840, location_name: 'United States' });
  const [device, setDevice] = useState('desktop');

  const addMutation = useMutation({
    mutationFn: addKeywords,
    onSuccess: (data) => {
      toast.success(`Copied ${data.added} keyword(s) to new location${data.skipped ? ` (${data.skipped} duplicates skipped)` : ''}`);
      onSuccess();
    },
    onError: (err) => toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to copy keywords'),
  });

  const handleCopy = (e) => {
    e.preventDefault();
    if (!targetProjectId) return toast.error('Select a project');
    if (!selectedKeywords.length) return toast.error('No keywords selected');

    addMutation.mutate({
      project_id: targetProjectId,
      keywords: selectedKeywords.map(kw => ({
        keyword: kw.keyword,
        location_code: location?.location_code || 2840,
        location_name: location?.location_name || 'United States',
        device,
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">Copy Keywords to Location</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleCopy} className="p-4 space-y-4">
          {/* Keywords being copied */}
          <div>
            <label className="block text-sm font-medium mb-1">Keywords ({selectedKeywords.length})</label>
            <div className="max-h-32 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2">
              {selectedKeywords.map(kw => (
                <div key={kw.id} className="flex items-center justify-between text-sm py-0.5">
                  <span className="font-mono">{kw.keyword}</span>
                  <span className="text-xs text-muted-foreground">{kw.location_name} · {kw.device}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Target Project */}
          <div>
            <label className="block text-sm font-medium mb-1">Target Project</label>
            <select
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              required
            >
              <option value="">Select project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* New Location */}
          <div>
            <label className="block text-sm font-medium mb-1">New Location</label>
            <LocationPicker value={location} onChange={(val) => setLocation(val || { location_code: 2840, location_name: 'United States' })} />
          </div>

          {/* Device */}
          <div>
            <label className="block text-sm font-medium mb-1">Device</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDevice('desktop')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
                  device === 'desktop' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                )}
              >
                <Monitor className="h-4 w-4" /> Desktop
              </button>
              <button
                type="button"
                onClick={() => setDevice('mobile')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
                  device === 'mobile' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                )}
              >
                <Smartphone className="h-4 w-4" /> Mobile
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={addMutation.isPending}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {addMutation.isPending ? 'Copying...' : `Copy ${selectedKeywords.length} Keyword${selectedKeywords.length !== 1 ? 's' : ''}`}
          </button>
        </form>
      </div>
    </div>
  );
}

function AddKeywordsModal({ projectId: initialProjectId, projects, onClose, onSuccess }) {
  const [projectId, setProjectId] = useState(initialProjectId || '');
  const [keywordsText, setKeywordsText] = useState('');
  const [location, setLocation] = useState({ location_code: 2840, location_name: 'United States' });
  const [device, setDevice] = useState('desktop');

  const addMutation = useMutation({
    mutationFn: addKeywords,
    onSuccess: (data) => {
      toast.success(data.message || 'Keywords added');
      onSuccess();
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to add keywords'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!projectId) return toast.error('Select a project');

    const keywordsList = keywordsText
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (!keywordsList.length) return toast.error('Enter at least one keyword');

    addMutation.mutate({
      project_id: projectId,
      keywords: keywordsList.map(keyword => ({
        keyword,
        location_code: location?.location_code || 2840,
        location_name: location?.location_name || 'United States',
        device,
      })),
    });
  };

  const keywordCount = keywordsText.split('\n').filter(k => k.trim()).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">Add Keywords</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Project */}
          <div>
            <label className="block text-sm font-medium mb-1">Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              required
            >
              <option value="">Select project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium mb-1">Keywords (one per line)</label>
            <textarea
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder={"best seo tools\nbacklink checker\nkeyword research tool"}
              rows={6}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono resize-y"
            />
            {keywordCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">{keywordCount} keyword(s)</p>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <LocationPicker value={location} onChange={(val) => setLocation(val || { location_code: 2840, location_name: 'United States' })} />
          </div>

          {/* Device */}
          <div>
            <label className="block text-sm font-medium mb-1">Device</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDevice('desktop')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
                  device === 'desktop' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                )}
              >
                <Monitor className="h-4 w-4" /> Desktop
              </button>
              <button
                type="button"
                onClick={() => setDevice('mobile')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
                  device === 'mobile' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                )}
              >
                <Smartphone className="h-4 w-4" /> Mobile
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={addMutation.isPending}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {addMutation.isPending ? 'Adding...' : `Add ${keywordCount || 0} Keyword${keywordCount !== 1 ? 's' : ''}`}
          </button>
        </form>
      </div>
    </div>
  );
}
